import {useMutation, useQueryClient} from "@tanstack/react-query";
import {
  setLocalStorageItem,
  LocalStorageError,
  LocalStorageErrorType,
} from "@/utils/localStorage";

interface SetLocalStorageParams {
  key: string;
  value: unknown;
}

interface UseSetLocalStorageMutationOptions {
  onSuccess?: (data: void, variables: SetLocalStorageParams) => void;
  onError?: (
    error: LocalStorageError,
    variables: SetLocalStorageParams,
  ) => void;
  invalidateQueries?: boolean;
}

/**
 * Query mutation hook for setting items in localStorage.
 */
export const useSetLocalStorageMutation = (
  options: UseSetLocalStorageMutationOptions = {},
) => {
  const queryClient = useQueryClient();
  const {onSuccess, onError, invalidateQueries = true} = options;

  return useMutation({
    mutationFn: async ({key, value}: SetLocalStorageParams): Promise<void> => {
      try {
        setLocalStorageItem(key, value);
        return Promise.resolve();
      } catch (error) {
        // Re-throw LocalStorageError as-is
        if (error instanceof LocalStorageError) {
          throw error;
        }

        // Wrap unknown errors
        throw new LocalStorageError(
          LocalStorageErrorType.UNKNOWN_ERROR,
          `Nepodařilo se nastavit položku localStorage "${key}": ${error}`,
          error as Error,
        );
      }
    },
    onSuccess: (data, variables) => {
      // Invalidate the specific localStorage query
      if (invalidateQueries) {
        queryClient.invalidateQueries({
          queryKey: ["localStorage", variables.key],
        });
      }

      // Call custom success handler
      onSuccess?.(data, variables);
    },
    onError: (error: LocalStorageError, variables) => {
      // Log error details
      console.error("localStorage mutation failed:", {
        type: error.type,
        message: error.message,
        key: variables.key,
        value: variables.value,
        originalError: error.originalError,
      });

      // Call custom error handler
      onError?.(error, variables);
    },
    retry: (failureCount, error) => {
      // Don't retry on quota exceeded or localStorage not available
      if (error instanceof LocalStorageError) {
        const nonRetryableErrors = [
          LocalStorageErrorType.NOT_AVAILABLE,
          LocalStorageErrorType.QUOTA_EXCEEDED,
          LocalStorageErrorType.SECURITY_ERROR,
        ];
        if (nonRetryableErrors.includes(error.type)) {
          return false;
        }
      }
      return failureCount < 2; // Retry up to 2 times for other errors
    },
  });
};
